import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import {
  FaShoppingCart,
  FaSpinner,
  FaPalette,
  FaRulerCombined,
  FaLayerGroup,
  FaSyncAlt,
  FaFile,
  FaCopy,
  FaCartArrowDown,
} from "react-icons/fa";
import { useCollege } from "../context/CollegeContext";
import { FaRobot } from "react-icons/fa";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { db, storage } from "../firebase/firebaseConfig";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { convertToPDF } from "../utility/fileConversion";
import { getDocument } from "pdfjs-dist/webpack";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import mammoth from "mammoth"; // For DOCX
import JSZip from "jszip"; // For PPTX
import { calculatePrice, calculatePriceforppt } from "../utility/Price_for_customization";
import { v4 as uuidv4 } from "uuid";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../App.css";

// Memoized DocViewer to prevent unnecessary re-renders
const MemoizedDocViewer = React.memo(({ filePreviewURL }) => (
  <DocViewer
    documents={[{ uri: filePreviewURL }]}
    pluginRenderers={DocViewerRenderers}
    config={{
      header: { disableHeader: true },
      loadingRenderer: {
        overrideComponent: () => (
          <div className="flex items-center justify-center h-full">
            <FaSpinner className="animate-spin text-indigo-500" />
          </div>
        ),
      },
    }}
    style={{ height: "100%", width: "100%" }}
  />
));

// Memoized File Preview Component
const FilePreview = React.memo(({ file }) => {
  const filePreviewURL = useMemo(() => URL.createObjectURL(file), [file]);
  const isSupported = file.type.startsWith("image/") || file.type === "application/pdf";

  return (
    <div className="w-full h-48 sm:h-72 border rounded-md shadow-md overflow-hidden flex items-center justify-center">
      {isSupported ? (
        file.type === "application/pdf" ? (
          <MemoizedDocViewer filePreviewURL={filePreviewURL} />
        ) : (
          <img src={filePreviewURL} alt="File Preview" className="w-full h-full object-contain" />
        )
      ) : (
        <p className="text-red-500 font-semibold text-center">
          File type not supported. Only PDF and images are allowed.
        </p>
      )}
    </div>
  );
});

// Helper function to parse page range
const parsePageRange = (pageRange, totalPages) => {
  let startPage = 1;
  let endPage = totalPages;

  if (pageRange) {
    const range = pageRange.split("-");
    startPage = parseInt(range[0], 10);
    endPage = range[1] ? parseInt(range[1], 10) : startPage;

    // Validate the range
    if (isNaN(startPage) || isNaN(endPage) || startPage < 1 || endPage > totalPages) {
      console.error("Invalid page range!");
      return { startPage: 1, endPage: totalPages };
    }
  }

  return { startPage, endPage };
};

const CustomizePDF = () => {
  const { darkMode } = useTheme();
  const { selectedCollege, uploadedFiles, setUploadedFiles } = useCollege();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const auth = getAuth();

  // State for customization properties for each file
  const [customizations, setCustomizations] = useState([]);
  // State for current file index in the carousel
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [imageCostSettings, setImageCostSettings] = useState({
    blackAndWhite: { A4: 2, A3: 5 },
    color: { A4: 5, A3: 10 },
  });
  // State for loading and upload progress
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // State for total pages of the current file
  const [filesPages, setFilesPages] = useState(Array(uploadedFiles.length).fill(0));
  // State for pricing data from Firestore
  const [pdfPrices, setPDFPrices] = useState(null);
  const [pptPrices, setPPTPrices] = useState(null);
  const [jpgPrices, setJPGPrices] = useState(null);
  // State for error message
  const [errorMessage, setErrorMessage] = useState("");
  // State for note checkbox and note input
  const [showNote, setShowNote] = useState(Array(uploadedFiles.length).fill(false));
  const [notes, setNotes] = useState(Array(uploadedFiles.length).fill(""));
  // State for binding
  const [bindingPrices, setBindingPrices] = useState({
    minPages: 30,
    fixedPrice: 30,
    perPagePrice: 0.5,
  });
  const [wantBinding, setWantBinding] = useState(Array(uploadedFiles.length).fill(false));

  // Fetch binding prices
  useEffect(() => {
    const fetchBindingPrices = async () => {
      try {
        const docRef = doc(db, "settings", "bindingPrices");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBindingPrices(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching binding prices:", error);
      }
    };
    fetchBindingPrices();
  }, []);

  // Fetch image cost settings
  useEffect(() => {
    const fetchImageCost = async () => {
      try {
        const docRef = doc(db, "settings", "imagePageCost");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setImageCostSettings({
            blackAndWhite: { A4: data.blackAndWhite?.A4 || 2, A3: data.blackAndWhite?.A3 || 5 },
            color: { A4: data.color?.A4 || 5, A3: data.color?.A3 || 10 },
          });
        }
      } catch (error) {
        console.error("Error fetching image cost:", error);
      }
    };
    fetchImageCost();
  }, []);

  // Fetch PDF pricing
  useEffect(() => {
    const fetchPDFPrices = async () => {
      try {
        const docRef = doc(db, "settings", "pdfCombinationPrices");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setPDFPrices(docSnap.data());
        } else {
          console.error("No PDF pricing data found in Firestore!");
        }
      } catch (error) {
        console.error("Error fetching PDF prices:", error);
      }
    };
    fetchPDFPrices();
  }, []);

  // Fetch PPT pricing
  useEffect(() => {
    const fetchPPTPrices = async () => {
      try {
        const docRef = doc(db, "settings", "pptCombinationPrices");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setPPTPrices(docSnap.data());
        } else {
          console.error("No PPT pricing data found in Firestore!");
        }
      } catch (error) {
        console.error("Error fetching PPT prices:", error);
      }
    };
    fetchPPTPrices();
  }, []);

  // Fetch JPG pricing
  useEffect(() => {
    const fetchJPGPrices = async () => {
      try {
        const docRef = doc(db, "settings", "jpgCombinationPrices");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setJPGPrices(docSnap.data());
        } else {
          console.error("No JPG pricing data found in Firestore!");
        }
      } catch (error) {
        console.error("Error fetching JPG prices:", error);
      }
    };
    fetchJPGPrices();
  }, []);

  // Function to detect images in PDF files
  const detectImagesInPDF = async (fileUrl) => {
    try {
      const response = await fetch(
        "https://pdf-api-353684517342.us-central1.run.app/detect-images",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_url: fileUrl }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to detect images in PDF.");
      }

      const data = await response.json();
      const pagesWithImages = Object.values(data).filter((value) => value !== "Text Only").length;
      return pagesWithImages;
    } catch (error) {
      console.error("Error detecting images in PDF:", error);
      return 0;
    }
  };

  // Handle file deletion
  const handleDeleteFile = (index) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);

    // Reset customizations, notes, and binding for the remaining files
    const updatedCustomizations = customizations.filter((_, i) => i !== index);
    const updatedNotes = notes.filter((_, i) => i !== index);
    const updatedWantBinding = wantBinding.filter((_, i) => i !== index);
    const updatedShowNote = showNote.filter((_, i) => i !== index);

    setCustomizations(updatedCustomizations);
    setNotes(updatedNotes);
    setWantBinding(updatedWantBinding);
    setShowNote(updatedShowNote);

    // Reset current file index if necessary
    if (currentFileIndex >= updatedFiles.length) {
      setCurrentFileIndex(updatedFiles.length - 1);
    }
  };

  // Redirect if no files are uploaded
  useEffect(() => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      toast.error("No files uploaded. Redirecting to home...");
      navigate("/home");
    } else {
      // Initialize customizations for each file
      const initialCustomizations = uploadedFiles.map(() => ({
        colorType: "Black & White",
        paperSize: "A4",
        paperType: "70GSM Paper",
        pagesPerSheet: "1",
        copies: "1",
        orientation: "Portrait",
        enableRange: false,
        pageRange: "",
      }));
      setCustomizations(initialCustomizations);
      setShowNote(Array(uploadedFiles.length).fill(false));
      setNotes(Array(uploadedFiles.length).fill(""));
      setWantBinding(Array(uploadedFiles.length).fill(false));
    }
  }, [uploadedFiles, navigate]);

  // Generate file preview URL and count pages for the current file
  // Replace the existing useEffect that counts pages with this:
  useEffect(() => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    const countPagesForAllFiles = async () => {
      const newPages = [...filesPages];

      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const fileType = file.type;
        let pages = 0;

        try {
          if (fileType === "application/pdf") {
            pages = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = async () => {
                try {
                  const pdf = await getDocument({ data: reader.result }).promise;
                  resolve(pdf.numPages);
                } catch (error) {
                  console.error("Error counting PDF pages:", error);
                  resolve(0);
                }
              };
              reader.readAsArrayBuffer(file);
            });
          } else if (
            fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ) {
            pages = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = async () => {
                const { value } = await mammoth.extractRawText({
                  arrayBuffer: reader.result,
                });
                const words = value.split(/\s+/).length;
                resolve(Math.ceil(words / 250));
              };
              reader.readAsArrayBuffer(file);
            });
          } else if (
            fileType === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
          ) {
            pages = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = async () => {
                const zip = await JSZip.loadAsync(reader.result);
                const slideFiles = Object.keys(zip.files).filter(
                  (file) => file.startsWith("ppt/slides/slide") && file.endsWith(".xml")
                );
                resolve(slideFiles.length);
              };
              reader.readAsArrayBuffer(file);
            });
          } else {
            pages = 1;
          }
        } catch (error) {
          console.error("Error processing file:", error);
          pages = 0;
        }

        newPages[i] = pages;
      }

      setFilesPages(newPages);
    };

    countPagesForAllFiles();
  }, [uploadedFiles]); // Only run when uploadedFiles changes

  // Add this useEffect to update the display when currentFileIndex changes
  useEffect(() => {
    if (filesPages.length > 0 && currentFileIndex < filesPages.length) {
      // You can use filesPages[currentFileIndex] wherever you need the current file's page count
    }
  }, [currentFileIndex, filesPages]);
  // Handle form changes for the current file
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomizations((prev) => {
      const updatedCustomizations = [...prev];
      updatedCustomizations[currentFileIndex] = {
        ...updatedCustomizations[currentFileIndex],
        [name]: value,
      };
      return updatedCustomizations;
    });
  };

  // Clear error message when pricing becomes valid
  useEffect(() => {
    if (errorMessage && !hasInvalidPricing()) {
      setErrorMessage("");
    }
  }, [customizations, wantBinding, filesPages, pdfPrices, pptPrices, jpgPrices, bindingPrices]);

  // Enhanced AI Scanning Animation Component
  const AIScanningAnimation = React.memo(() => {
    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-300"
      >
        <motion.div
          animate={{
            rotate: [0, 15, -15, 0],
            scale: [1, 1.1, 1],
            transition: {
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
        >
          <FaRobot className="text-indigo-500" />
        </motion.div>
        <span>Processing - AI Analyzing your files</span>
        <div className="flex space-x-1 ml-1">
          {[1, 2, 3].map((dot) => (
            <motion.span
              key={dot}
              animate={{
                y: [0, -2, 0],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: dot * 0.2,
              }}
              className="block w-1.5 h-1.5 rounded-full bg-indigo-400"
            />
          ))}
        </div>
      </motion.div>
    );
  });

  // Check if all files have valid customizations
  const areAllFilesValid = () => {
    return customizations.every(
      (customization) =>
        customization.colorType !== "Select" &&
        customization.paperSize !== "Select" &&
        customization.paperType !== "Select" &&
        customization.pagesPerSheet.trim() !== "" &&
        customization.copies.trim() !== ""
    );
  };

  // Check for invalid pricing
const hasInvalidPricing = () => {
  return uploadedFiles.some((file, index) => {
    const customization = customizations[index];
    const currentWantBinding = wantBinding[index];
    const price = calculatePrice(
      customization,
      filesPages[index] || 0, // Use filesPages[index] here
      file.type.startsWith("image/")
        ? jpgPrices
        : file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        ? pptPrices
        : pdfPrices,
      currentWantBinding,
      bindingPrices,
      file.type
    );
    return price <= 0;
  });
};

  // Handle adding to cart
  const handleAddToCart = async () => {
    if (!uploadedFiles || uploadedFiles.length === 0 || !selectedCollege) {
      toast.error("Please fill all the details properly!");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      toast.error("User not authenticated!");
      return;
    }

    if (!areAllFilesValid()) {
      setErrorMessage("Please fill all the details for all files before proceeding.");
      return;
    }

    if (hasInvalidPricing()) {
      setErrorMessage(
        "Some files have pricing issues. Please check your customizations or contact support."
      );
      return;
    }

    // Validate all page ranges
    for (let i = 0; i < uploadedFiles.length; i++) {
      const customization = customizations[i];
      if (customization.enableRange && customization.pageRange) {
        const range = customization.pageRange.split("-");
        const startPage = parseInt(range[0], 10);
        const endPage = range[1] ? parseInt(range[1], 10) : startPage;

if (isNaN(startPage) || isNaN(endPage) || startPage < 1 || endPage > filesPages[i]) {
  toast.error(`Invalid page range in file ${i + 1}!`);
  return;
}
      }
    }

    setIsLoading(true);
    setUploadProgress(0);

    try {
      const userId = user.uid;
      const cartItems = [];
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 1000;

      const uploadFileWithRetry = async (file, i, attempt = 1) => {
        const customization = customizations[i];
        const note = notes[i];
        const fileType = file.type;
        const currentWantBinding = wantBinding[i];

        try {
          const randomFileName = `${uuidv4()}.${file.name.split(".").pop()}`;
          let fileToUpload = file;

          if (
            !fileType.startsWith("image/") &&
            fileType !== "application/pdf" &&
            fileType !== "application/vnd.openxmlformats-officedocument.presentationml.presentation"
          ) {
            fileToUpload = await convertToPDF(file);
          }

          const storageRef = ref(storage, `Uploads/${userId}/${randomFileName}`);
          const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

          let fileProgress = 0;

          const downloadURL = await new Promise((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                fileProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress((prev) => {
                  const totalSize = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
                  const weight = file.size / totalSize;
                  return prev + (fileProgress - prev * weight) * weight;
                });
              },
              (error) => reject(error),
              async () => {
                try {
                  const url = await getDownloadURL(uploadTask.snapshot.ref);
                  resolve(url);
                } catch (error) {
                  reject(error);
                }
              }
            );
          });

          let totalPrice;
          if (
            fileType === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
          ) {
            totalPrice = calculatePriceforppt(
              customization,
              filesPages[i],
              pptPrices,
              currentWantBinding,
              bindingPrices
            );
          } else if (fileType.startsWith("image/")) {
            totalPrice = calculatePrice(
              customization,
              filesPages[i],
              jpgPrices,
              currentWantBinding,
              bindingPrices,
              fileType
            );
          } else {
            const basePrice = calculatePrice(
              customization,
              filesPages[i],
              pdfPrices,
              currentWantBinding,
              bindingPrices,
              fileType
            );
            let imagePagesCost = 0;

            if (fileType === "application/pdf") {
              const pagesWithImages = await detectImagesInPDF(downloadURL);
              const imageCostPerPage =
                customization.colorType === "Color"
                  ? imageCostSettings.color[customization.paperSize]
                  : imageCostSettings.blackAndWhite[customization.paperSize];

              imagePagesCost = pagesWithImages * imageCostPerPage * customization.copies;
            }

            totalPrice = basePrice + imagePagesCost;
          }

          const cartItem = {
            userId,
            fileName: randomFileName,
            fileURL: downloadURL,
            selectedCollege,
            ...customization,
            price: totalPrice,
            timestamp: new Date(),
            totalPages: filesPages[i],
            originalName: file.name,
            note: note,
            pageRange: customization.enableRange ? customization.pageRange : "all",
            fileType: fileType,
            wantBinding: currentWantBinding,
            ...(currentWantBinding && {
              bindingPrice: (() => {
                const pagesInDocument = customization.enableRange
                  ? (() => {
                      const { startPage, endPage } = parsePageRange(
                        customization.pageRange,
                        filesPages[i]
                      );
                      return endPage - startPage + 1;
                    })()
                  : filesPages[i];

                return (
                  customization.copies *
                  (() => {
                    const pagesInDocument = customization.enableRange
                      ? (() => {
                          const { startPage, endPage } = parsePageRange(
                            customization.pageRange,
                            filesPages[i] // Use filesPages[i] here
                          );
                          return endPage - startPage + 1;
                        })()
                      : filesPages[i]; // And here

                    return pagesInDocument <= bindingPrices.minPages
                      ? bindingPrices.fixedPrice
                      : bindingPrices.fixedPrice +
                          (pagesInDocument - bindingPrices.minPages) * bindingPrices.perPagePrice;
                  })()
                );
              })(),
            }),
            pagesPerCopy: customization.enableRange
              ? (() => {
                  const { startPage, endPage } = parsePageRange(
                    customization.pageRange,
                    filesPages[i]
                  );
                  return endPage - startPage + 1;
                })()
              : filesPages[i],
          };

          return cartItem;
        } catch (error) {
          if (attempt < MAX_RETRIES) {
            console.warn(`Retrying file ${file.name} (attempt ${attempt + 1})`);
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * attempt));
            return uploadFileWithRetry(file, i, attempt + 1);
          }
          throw new Error(
            `Failed to upload ${file.name} after ${MAX_RETRIES} attempts: ${error.message}`
          );
        }
      };

      const uploadPromises = uploadedFiles.map((file, i) => uploadFileWithRetry(file, i));
      setUploadProgress(0.1);

      const cartItemsToAdd = await Promise.all(uploadPromises);

      const addToFirestoreWithRetry = async (item, attempt = 1) => {
        try {
          const docRef = await addDoc(collection(db, "Cart", userId, "Items"), item);
          return {
            ...item,
            id: docRef.id,
          };
        } catch (error) {
          if (attempt < MAX_RETRIES) {
            console.warn(
              `Retrying Firestore add for ${item.originalName} (attempt ${attempt + 1})`
            );
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * attempt));
            return addToFirestoreWithRetry(item, attempt + 1);
          }
          throw new Error(
            `Failed to add ${item.originalName} to Firestore after ${MAX_RETRIES} attempts: ${error.message}`
          );
        }
      };

      const completedItems = await Promise.all(
        cartItemsToAdd.map((item) => addToFirestoreWithRetry(item))
      );
      cartItems.push(...completedItems);

      addToCart(cartItems);
      toast.success("All files added to cart successfully!");
      navigate("/cart");
    } catch (error) {
      console.error("Order processing error:", error);
      toast.error(`Failed to process order: ${error.message}`);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  // Slider settings for peek carousel
  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: uploadedFiles.length > 1,
    centerPadding: uploadedFiles.length > 1 ? "10%" : "0",
    afterChange: (index) => setCurrentFileIndex(index),
    arrows: uploadedFiles.length > 1,
    prevArrow: (
      <button
        className={`slick-prev rounded-full w-8 h-8 flex items-center justify-center ${
          darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-white hover:bg-gray-100"
        } shadow-md`}
        style={{ left: "-35px", zIndex: 1 }}
      >
        <i className={`fas fa-chevron-left ${darkMode ? "text-white" : "text-gray-800"}`} />
      </button>
    ),
    nextArrow: (
      <button
        className={`slick-next rounded-full w-8 h-8 flex items-center justify-center ${
          darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-white hover:bg-gray-100"
        } shadow-md`}
        style={{ right: "-35px", zIndex: 1 }}
      >
        <i className={`fas fa-chevron-right ${darkMode ? "text-white" : "text-gray-800"}`} />
      </button>
    ),
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 sm:p-6 ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"
      }`}
    >
      <div
        className={`w-full max-w-5xl p-6 rounded-lg shadow-lg flex flex-col lg:flex-row gap-6 ${
          darkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Carousel at the top for mobile */}
        <div className="w-full lg:hidden">
          <h3 className="text-md sm:text-lg font-semibold text-indigo-500 mb-2">File Preview</h3>
          <Slider {...sliderSettings} className="w-full">
            {uploadedFiles.map((file, index) => (
              <div key={index} className="px-2">
                <FilePreview file={file} onDelete={() => handleDeleteFile(index)} />
              </div>
            ))}
          </Slider>
        </div>

        {/* Left Section (Form) */}
        <div className="flex-1">
          <h2 className="text-lg sm:text-xl font-semibold">Customize Print</h2>
          <h2 className="text-lg sm:text-xl font-semibold mb-4">
            File: {uploadedFiles[currentFileIndex]?.name}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                label: "Color Type",
                name: "colorType",
                options: ["Select", "Color", "Black & White"],
                icon: <FaPalette className="text-indigo-500" />,
              },
              {
                label: "Paper Size",
                name: "paperSize",
                options: ["Select", "A4", "A3"],
                icon: <FaRulerCombined className="text-blue-500" />,
              },
              {
                label: "Paper Type",
                name: "paperType",
                options: [
                  "Select",
                  "70GSM Paper",
                  "100GSM Paper",
                  "Bond Paper",
                  "Cartridge Paper",
                  "Glossy Paper",
                ],
                icon: <FaLayerGroup className="text-green-500" />,
              },
              {
                label: "Orientation",
                name: "orientation",
                options: ["Portrait", "Landscape"],
                icon: <FaSyncAlt className="text-yellow-500" />,
              },
            ].map(({ label, name, options, icon }) => (
              <div key={name} className="flex flex-col">
                <label className="block text-sm font-medium flex items-center gap-2">
                  {icon} {label}:
                </label>
                <select
                  name={name}
                  value={customizations[currentFileIndex]?.[name] || "Select"}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-md bg-gray-200 dark:bg-gray-700"
                >
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            {[
              {
                label: "Pages per Sheet",
                name: "pagesPerSheet",
                icon: <FaFile className="text-purple-500" />,
                isSelect: true,
                options:
                  uploadedFiles[currentFileIndex]?.type ===
                  "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    ? ["1", "2", "4"]
                    : ["1", "2", "4", "6", "9"], // Same options for PDF and images
              },
              {
                label: "No. of Copies",
                name: "copies",
                icon: <FaCopy className="text-pink-500" />,
                isSelect: false,
              },
            ].map(({ label, name, icon, isSelect, options }) => (
              <div key={name} className="flex flex-col">
                <label className="block text-sm font-medium flex items-center gap-2">
                  {icon} {label}:
                </label>
                {isSelect ? (
                  <select
                    name={name}
                    value={customizations[currentFileIndex]?.[name] || "1"}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-md bg-gray-200 dark:bg-gray-700"
                  >
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number"
                    name={name}
                    min="1"
                    value={customizations[currentFileIndex]?.[name] || ""}
                    onChange={handleChange}
                    placeholder="Enter the number"
                    className="w-full p-2 border rounded-md bg-gray-200 dark:bg-gray-700"
                  />
                )}
              </div>
            ))}
          </div>
          {/* Enable Page Range Checkbox and Input */}
          <div className="flex flex-col mt-1">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={customizations[currentFileIndex]?.enableRange || false}
                onChange={(e) => {
                  const updatedCustomizations = [...customizations];
                  updatedCustomizations[currentFileIndex] = {
                    ...updatedCustomizations[currentFileIndex],
                    enableRange: e.target.checked,
                    pageRange: e.target.checked ? "1-1" : "",
                  };
                  setCustomizations(updatedCustomizations);
                }}
                className="mr-2"
              />
              Enable Page Range
            </label>

            {customizations[currentFileIndex]?.enableRange && (
              <div className="mt-2">
                <label className="block text-sm font-medium flex items-center gap-2">
                  <FaFile className="text-purple-500" /> Page Range (e.g., 1-4):
                </label>
                <input
                  type="text"
                  name="pageRange"
                  value={customizations[currentFileIndex]?.pageRange || ""}
                  onChange={handleChange}
                  placeholder="e.g., 1-4"
                  className="w-full p-2 border rounded-md bg-gray-200 dark:bg-gray-700"
                />
              </div>
            )}
          </div>
          {/* Binding Option */}
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id={`wantBinding-${currentFileIndex}`}
              checked={wantBinding[currentFileIndex] || false}
              onChange={(e) => {
                const updatedWantBinding = [...wantBinding];
                updatedWantBinding[currentFileIndex] = e.target.checked;
                setWantBinding(updatedWantBinding);
              }}
              className="mr-2"
            />
            <label htmlFor={`wantBinding-${currentFileIndex}`} className="font-medium">
              Want Binding?{" "}
              {wantBinding[currentFileIndex] && (
                <span className="text-sm text-gray-600 ml-1">
                  (₹
                  {(() => {
                    if (!bindingPrices || !customizations[currentFileIndex]?.copies) return "0";

                    const pagesInDocument = customizations[currentFileIndex]?.enableRange
                      ? (() => {
                          const { startPage, endPage } = parsePageRange(
                            customizations[currentFileIndex].pageRange,
                            filesPages[currentFileIndex] || 0
                          );
                          return endPage - startPage + 1;
                        })()
                      : filesPages[currentFileIndex] || 0;

                    const pricePerCopy =
                      pagesInDocument <= bindingPrices.minPages
                        ? bindingPrices.fixedPrice
                        : bindingPrices.fixedPrice +
                          (pagesInDocument - bindingPrices.minPages) * bindingPrices.perPagePrice;

                    return `${pricePerCopy} × ${customizations[currentFileIndex].copies} = ₹${
                      pricePerCopy * customizations[currentFileIndex].copies
                    }`;
                  })()}
                  )
                </span>
              )}
            </label>
          </div>
          {/* Note Section */}
          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showNote[currentFileIndex]}
                onChange={(e) => {
                  const updatedShowNote = [...showNote];
                  updatedShowNote[currentFileIndex] = e.target.checked;
                  setShowNote(updatedShowNote);
                }}
                className="mr-2"
              />
              Add a note for this file
            </label>

            {showNote[currentFileIndex] && (
              <textarea
                value={notes[currentFileIndex]}
                onChange={(e) => {
                  const updatedNotes = [...notes];
                  updatedNotes[currentFileIndex] = e.target.value;
                  setNotes(updatedNotes);
                }}
                className="w-full Kilometers traveled: 1000000
p-2 mt-2 border rounded-md bg-gray-200 dark:bg-gray-700"
                rows={4}
              />
            )}
          </div>

          {/* Price and Page Information */}
          <div className="mt-4 flex flex-col sm:flex-row items-center sm:justify-between gap-3">
            {(pdfPrices || pptPrices || jpgPrices) && (
              <div className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                <span className="text-gray-700 dark:text-gray-300">Total Price:</span>
                <span className="text-green-500">
                  ₹
                  {uploadedFiles[currentFileIndex]?.type ===
                  "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    ? calculatePriceforppt(
                        customizations[currentFileIndex],
                        filesPages[currentFileIndex] || 0,
                        pptPrices,
                        wantBinding[currentFileIndex],
                        bindingPrices
                      )
                    : uploadedFiles[currentFileIndex]?.type.startsWith("image/")
                    ? calculatePrice(
                        customizations[currentFileIndex],
                        filesPages[currentFileIndex] || 0,
                        jpgPrices,
                        wantBinding[currentFileIndex],
                        bindingPrices,
                        uploadedFiles[currentFileIndex]?.type
                      )
                    : calculatePrice(
                        customizations[currentFileIndex],
                        filesPages[currentFileIndex] || 0,
                        pdfPrices,
                        wantBinding[currentFileIndex],
                        bindingPrices,
                        uploadedFiles[currentFileIndex]?.type
                      )}
                </span>
              </div>
            )}

            {filesPages[currentFileIndex] > 0 && (
              <div className="flex sm:flex-row gap-2">
                <div className="text-lg font-semibold sm:text-sm flex items-center gap-2">
                  <span className="text-gray-700 dark:text-gray-300">Pages per copy:</span>
                  <span className="text-blue-500">
                    {customizations[currentFileIndex]?.enableRange
                      ? (() => {
                          const { startPage, endPage } = parsePageRange(
                            customizations[currentFileIndex].pageRange,
                            filesPages[currentFileIndex] || 0
                          );
                          return `${endPage - startPage + 1}`;
                        })()
                      : filesPages[currentFileIndex] || 0}
                  </span>
                </div>
                <div className="text-lg font-semibold sm:text-sm flex items-center gap-2">
                  <span className="text-gray-700 dark:text-gray-300">Copies:</span>
                  <span className="text-blue-500">
                    {customizations[currentFileIndex]?.copies || "1"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {isLoading && (
            <div className="mt-4 space-y-3">
              {(uploadedFiles[currentFileIndex]?.type === "application/pdf" ||
                uploadedFiles[currentFileIndex]?.type ===
                  "application/vnd.openxmlformats-officedocument.presentationml.presentation") &&
                uploadProgress > 0 &&
                uploadProgress < 100 && (
                  <AIScanningAnimation fileType={uploadedFiles[currentFileIndex]?.type} />
                )}

              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200">
                      {uploadProgress < 100 ? "Uploading" : "Processing"}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-indigo-600">
                      {uploadProgress.toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div
                  className="overflow-hidden h-2 Taxes Paid: $0
mb-4 text-xs flex rounded-full bg-indigo-200"
                >
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                    transition={{ duration: 0.5 }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-indigo-500 to-purple-500"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {uploadProgress < 100 ? "Uploading your file..." : "Finalizing processing..."}
              </p>
            </div>
          )}
          {errorMessage && (
            <div className="w-[fit-content] mt-1 bg-red-100 border text-red-700 px-4 py-3 rounded-lg mb-4">
              {errorMessage}
            </div>
          )}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAddToCart}
              disabled={isLoading}
              className={`flex items-center justify-center gap-2 px-4 py-2 text-sm sm:text-base rounded-md ${
                !isLoading
                  ? "bg-indigo-500 text-white hover:scale-105"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
              } w-full sm:w-auto`}
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin" /> Adding...
                </>
              ) : (
                <>
                  <FaShoppingCart className="text-white" /> Add to Cart
                </>
              )}
            </button>

            <button
              onClick={() => navigate("/cart")}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm sm:text-base bg-green-500 text-white rounded-md hover:scale-105 w-full sm:w-auto"
            >
              <FaCartArrowDown className="text-white" /> View Cart
            </button>
          </div>
        </div>

        {/* Carousel at the bottom for desktop */}
        {uploadedFiles.length > 0 && (
          <div className="w-full lg:w-1/3 hidden lg:flex flex-col items-center justify-center pt-4 lg:pt-0 lg:pl-4">
            <h3 className="text-md sm:text-lg font-semibold text-indigo-500 mb-2">File Preview</h3>
            <Slider {...sliderSettings} className="w-full">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="px-2">
                  <FilePreview file={file} onDelete={() => handleDeleteFile(index)} />
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomizePDF;
